import {AiToolImpl} from "@me8eon/aiclient";
import {mustBeObjectWithFields, mustBeString} from "@me8eon/validate";
import {ElasticSearchConfig, ElasticSearchHit, ElasticSearchKnnSearchResponse} from "@me8eon/ai.tools.utils";
import { EnvironmentConfig } from "@me8eon/config";
import { JwtTokenFn } from "@me8eon/authentication";

/**
 * Converts an Elasticsearch hit to a formatted string.
 * @param hit
 * @param index
 */
const hitToString = (hit: ElasticSearchHit, index: number): string => {
    return `**Document ${index + 1}**:
     ${hit._source.title && `- Title: ${hit._source.title}\n`}
     - Score: ${hit._score}, 
     - Index: ${hit._index}, 
     - ID: ${hit._id}
     - Source: """${JSON.stringify(hit._source)}"""`;
};

type ToolArgs = {
    query: string
}

type ToolImpl = AiToolImpl<ToolArgs>

type Tool = (config: ElasticSearchConfig, jwtTokenFn: JwtTokenFn) => ToolImpl

export const EonKnowledgeBaseSearchToolImpl: Tool = (esConfig, jwtTokenFn) => {
    return {
        type: "eonKnowledgeBaseSearch",
        toolDefn: {
            type: 'function',
            name: "eonKnowledgeBaseSearch",
            description: "This tool performs a deep search on specified Elasticsearch index using semantic_text fields- one field with vector (e5-small) and another with sparse (elser). It retrieves the most relevant documents based on the provided query.",
            parameters: {
                type: "object",
                required: ["query"],
                additionalProperties: false,
                properties: {
                    query: {
                        type: "string",
                        description: "The search query string used to find relevant documents. Should be a concise and clear representation of the information being sought. This query HAS TO BE IN ENGLISH as the underlying models are trained primarily on English data.",
                    }
                },
            },
        },
        validateArgs: mustBeObjectWithFields({query: mustBeString}),
        execute: async (indices, input) => {
            try {
                const {
                    axios,
                    blacklistedFields = [],
                } = esConfig;
                const {query} = input;
                if (indices.length === 0) throw new Error("No indicies provided to knn tool");
                const top = 5;
                const indiciesAsString = indices.join(",");
                const compassIndex = "compass-prod-enhanced"
                const urlKbSearch = `${EnvironmentConfig.elasticSearchProxyUrl}/${indiciesAsString}/_search`;
                const urlCompassSearch = `${EnvironmentConfig.elasticSearchProxyUrl}/${compassIndex}/_search`;
                const headers = {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${await jwtTokenFn()}`,
                };



                const body = {
                    "_source": {
                        "excludes": [...blacklistedFields,
                            "denied_countries",
                            "denied_companies",
                            "denied_users",
                            "allowed_countries",
                            "allowed_companies",
                            "allowed_users",
                            "allowed_all_countries",
                            "allowed_all_companies",
                            "allowed_all_users",
                            "dense_vector",
                            "sparse_vector"],
                    },
                    "size": top,
                    "retriever": {
                        "text_similarity_reranker": {
                            "retriever": {
                                "rrf": {
                                    "rank_window_size": 100,
                                    "rank_constant": 60,
                                    "retrievers": [
                                        {
                                            "standard": {
                                                "query": {
                                                    "match": {
                                                        "sparse_vector": query,
                                                    }
                                                }
                                            }
                                        },
                                        {
                                            "standard": {
                                                "query": {
                                                    "match": {
                                                        "dense_vector": query,
                                                    }
                                                }
                                            }
                                        }
                                    ]
                                }
                            },
                            "field": "semantic_text",
                            "inference_id": "cross-encoder-rerank",
                            "inference_text": query,
                            "rank_window_size": 5
                        },
                    },
                    "highlight": {
                        "fields": {
                            "sparse_vector": {
                                "type": "semantic",
                                "number_of_fragments": 3,
                                "order": "score"
                            },
                            "dense_vector": {
                                "type": "semantic",
                                "number_of_fragments": 3,
                                "order": "score"
                            }
                        }
                    }
                };


                const [kbResponse, compassResponse] = await Promise.allSettled([
                    axios.post<ElasticSearchKnnSearchResponse>(urlKbSearch, body, {headers: headers}),
                    axios.post<ElasticSearchKnnSearchResponse>(urlCompassSearch, body, {headers: headers})
                ]);

                let content = '';
                if (kbResponse.status === 'fulfilled' && kbResponse.value.data?.hits?.hits) {
                    const topKbDocuments = kbResponse.value.data.hits.hits.slice(0, top).map((hit, index) => hitToString(hit, index));
                    content = `Here are the top ${topKbDocuments.length} documents found:\n\n${topKbDocuments.join("\n\n")}`;
                }

                let compassContent = '';
                if (compassResponse.status === 'fulfilled' && compassResponse.value.data?.hits?.hits) {
                    const topCompassDocuments = compassResponse.value.data.hits.hits.slice(0, top).map((hit, index) => hitToString(hit, index));
                    compassContent = `Here's additional content: \n\n${topCompassDocuments.join("\n\n")}`;
                }

                return {
                    value: content + compassContent
                }

            } catch (e: any) {
                return {
                    value: "An error occurred while performing the deep search.",
                    _meta: {
                        errorMessage: String(e?.message ?? e),
                    }
                };
            }

        },
    };
};

